import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import { API } from '@/utils/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [formLoadedAt] = useState(() => Date.now());

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Honeypot check — if the hidden field has a value, silently "succeed" without sending
    const honeypot = e.target.elements['website_url'];
    if (honeypot && honeypot.value) {
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/contact`, {
        ...formData,
        _t: formLoadedAt
      });
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Phone className="w-6 h-6 text-green-600" />,
      title: 'Phone',
      details: ['01392 980077', '07977 215934'],
      links: ['tel:01392980077', 'tel:07977215934']
    },
    {
      icon: <Mail className="w-6 h-6 text-cyan-500" />,
      title: 'Email',
      details: ['info@energyperformancedirect.co.uk'],
      links: ['mailto:info@energyperformancedirect.co.uk']
    },
    {
      icon: <MapPin className="w-6 h-6 text-green-600" />,
      title: 'Location',
      details: ['Exeter, Devon, UK'],
      links: [null]
    },
    {
      icon: <Clock className="w-6 h-6 text-cyan-500" />,
      title: 'Hours',
      details: ['Monday - Friday: 9am - 5pm', 'Saturday: By appointment'],
      links: [null, null]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="Contact Energy Performance Direct | Exeter Energy Assessors"
        description="Get in touch with Energy Performance Direct for EPCs, SAP calculations, and energy assessments in Exeter and South West England. Call 01392 980077 or book online."
        keywords="contact EPC Exeter, energy assessor contact, EPC booking Exeter, SAP calculations enquiry"
        canonical="/contact"
      />
      <Breadcrumbs items={[
        { name: 'Contact', path: '/contact' }
      ]} />
      <Navbar />

      {/* Header */}
      <section className="py-16" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 text-white" data-testid="contact-heading">
              Contact Us
            </h1>
            <p className="text-base sm:text-lg opacity-95 text-white">
              Get in touch with our team
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Get In Touch</h2>
              <p className="text-gray-700 mb-8">
                Have a question or ready to book? Contact us using any of the methods below, and we'll get back to you as soon as possible.
              </p>

              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <Card key={index} className="border-none shadow-md">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-gradient-to-br from-green-100 to-cyan-100 rounded-full">
                          {item.icon}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                          {item.details.map((detail, idx) => (
                            item.links[idx] ? (
                              <a
                                key={idx}
                                href={item.links[idx]}
                                className="block text-sm text-gray-600 hover:text-green-600 mb-1"
                              >
                                {detail}
                              </a>
                            ) : (
                              <p key={idx} className="text-sm text-gray-600 mb-1">{detail}</p>
                            )
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="border-none shadow-lg">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot field — hidden from humans, filled by bots */}
                    <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', overflow: 'hidden', height: 0, width: 0 }} aria-hidden="true" tabIndex={-1}>
                      <label htmlFor="website_url">Website</label>
                      <input type="text" id="website_url" name="website_url" autoComplete="off" tabIndex={-1} />
                    </div>
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        data-testid="contact-name-input"
                        className="mt-1"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        data-testid="contact-email-input"
                        className="mt-1"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone (Optional)</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        data-testid="contact-phone-input"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        data-testid="contact-message-input"
                        className="mt-1"
                        rows={5}
                        required
                      />
                    </div>

                    {/* GDPR Notice */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs">
                      <p className="text-gray-700">
                        By submitting this form, you consent to us processing your data in accordance with UK GDPR. View our{' '}
                        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline font-medium">
                          Privacy Policy
                        </a>.
                      </p>
                    </div>

                    <Button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit-btn"
                      className="w-full text-white"
                      style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}
                    >
                      {submitting ? 'Sending...' : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;